import { useEffect, useState, type FC, type ReactNode } from 'react';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Image } from '../../components/image';

export interface MarkdownRendererProps {
  contentPath: string;
}

export const MarkdownRenderer: FC<MarkdownRendererProps> = ({ contentPath }) => {
  const [content, setContent] = useState<ReactNode | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        const response = await fetch(contentPath);
        const text = await response.text();
        setContent(convertMarkdownToHTML(text.replace(/'/g, '\u2019')));
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading markdown:', error);
        setIsLoading(false);
      }
    };

    loadMarkdown();
  }, [contentPath]);

  if (isLoading) {
    return <div className='markdown-loading'>Loading...</div>;
  }

  return <div className='markdown-content'>{content}</div>;
};

function isLocalImage(src: string | undefined): boolean {
  return !!src && src.startsWith('/') && /\.(png|jpe?g)$/i.test(src);
}

function convertMarkdownToHTML(markdown: string): ReactNode {
  return (
    <Markdown
      rehypePlugins={[rehypeRaw]}
      remarkPlugins={[remarkGfm]}
      components={{
        img: ({ src, alt, ...props }) => {
          if (isLocalImage(src)) {
            return <Image src={src!} alt={alt ?? ''} {...props} />;
          }
          return <img src={src} alt={alt} {...props} />;
        },
      }}
    >
      {markdown}
    </Markdown>
  );
}
